/**
 * Priority Button Component
 * "+" button that opens priority toggle menu
 * Supports both click and hover (desktop) to open
 */

import { useState, useRef, useEffect } from 'react'
import { PriorityToggleMenu } from './PriorityToggleMenu'

interface PriorityButtonProps {
  priorityEnabled: boolean
  onToggle: (enabled: boolean) => void
  onTipClick: () => void
  disabled?: boolean
}

export function PriorityButton({
  priorityEnabled,
  onToggle,
  onTipClick,
  disabled = false
}: PriorityButtonProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [wasClicked, setWasClicked] = useState(false) // Track if opened via click
  const containerRef = useRef<HTMLDivElement>(null)
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Detect if device supports hover (desktop)
  const isDesktop = typeof window !== 'undefined' && window.matchMedia('(hover: hover)').matches

  // Handle click
  const handleClick = () => {
    if (!disabled) {
      const newState = !isMenuOpen
      setIsMenuOpen(newState)
      setWasClicked(newState)
    }
  }

  // Handle hover enter (desktop only)
  const handleMouseEnter = () => {
    if (!disabled && isDesktop) {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current)
        hoverTimeoutRef.current = null
      }
      if (!isMenuOpen) {
        setIsMenuOpen(true)
      }
    }
  }

  // Handle hover leave (desktop only)
  const handleMouseLeave = () => {
    if (isDesktop && !wasClicked) {
      hoverTimeoutRef.current = setTimeout(() => {
        setIsMenuOpen(false)
      }, 150)
    }
  }

  // Handle menu close
  const handleClose = () => {
    setIsMenuOpen(false)
    setWasClicked(false)
  }

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current)
      }
    }
  }, [])

  return (
    <div
      ref={containerRef}
      className="relative"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <button
        type="button"
        onClick={handleClick}
        disabled={disabled}
        className={`p-3 rounded-full transition-all duration-200 ${
          disabled
            ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
            : priorityEnabled
            ? 'bg-gradient-to-r from-amber-500 to-yellow-500 text-white shadow-md hover:from-amber-600 hover:to-yellow-600'
            : 'bg-amber-50 text-amber-600 hover:bg-amber-100 border border-amber-200'
        }`}
        title={priorityEnabled ? "Buzz mode enabled - Click to toggle" : "Buzz Mini's Phone"}
      >
        {priorityEnabled ? (
          // Star when enabled (matches priority message badge)
          <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
        ) : (
          // Plus when disabled
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
        )}
      </button>

      <PriorityToggleMenu
        isOpen={isMenuOpen}
        onClose={handleClose}
        priorityEnabled={priorityEnabled}
        onToggle={onToggle}
        onTipClick={onTipClick}
        showBackdrop={wasClicked}
      />
    </div>
  )
}
